import { Button } from '@tempo-io/sui-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tempo-io/sui-dialog';
import { useTranslation } from 'react-i18next';
import { LifecycleAction, type LifecycleActionType } from './comments/types';

type LifecycleActionDialogProps = {
  entityLabel: string;
  isOpen: boolean;
  lifecycleAction: LifecycleActionType;
  details?: string;
  onClose: () => void;
  onSubmit: () => void;
};

export const LifecycleActionDialog = ({
  entityLabel,
  isOpen,
  lifecycleAction,
  details,
  onClose,
  onSubmit,
}: LifecycleActionDialogProps) => {
  const { t } = useTranslation('translation');

  const content = {
    [LifecycleAction.Archive]: {
      title: t(($) => $.intake.general.archiveEntity, { entityLabel }),
      warning: t(($) => $.intake.general.areYouSureYouWantToArchiveThisEntity, { entityLabel }),
      buttonLabel: t(($) => $.intake.general.archive),
    },
    [LifecycleAction.Restore]: {
      title: t(($) => $.intake.general.restoreEntity, { entityLabel }),
      warning: t(($) => $.intake.general.areYouSureYouWantToRestoreThisEntity, { entityLabel }),
      buttonLabel: t(($) => $.intake.general.restore),
    },
    [LifecycleAction.Delete]: {
      title: t(($) => $.intake.general.deleteEntity, { entityLabel }),
      warning: t(($) => $.intake.general.areYouSureYouWantToDeleteThisEntity, { entityLabel }),
      buttonLabel: t(($) => $.intake.general.delete),
    },
  }[lifecycleAction];

  const handleSubmit = () => {
    onSubmit();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle data-testid="fim3-lifecycle-dialog-title">{content.title}</DialogTitle>
        </DialogHeader>
        <DialogDescription asChild>
          <div data-testid="fim3-lifecycle-dialog-description">
            {content.warning}
            {lifecycleAction === LifecycleAction.Delete && (
              <p className="mt-4">{t(($) => $.intake.general.thisActionCannotBeUndone)}</p>
            )}
          </div>
        </DialogDescription>
        {details && (
          <p
            className="text-sm leading-normal text-text-warning"
            data-testid="fim3-lifecycle-dialog-details"
          >
            {details}
          </p>
        )}
        <DialogFooter>
          <Button data-testid="fim3-lifecycle-dialog-cancel" variant="ghost" onClick={onClose}>
            {t(($) => $.common.cancel)}
          </Button>
          <Button
            data-testid="fim3-lifecycle-dialog-submit"
            variant={lifecycleAction === LifecycleAction.Delete ? 'destructive' : 'default'}
            onClick={handleSubmit}
            autoFocus
          >
            {content.buttonLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
